import { serializeParams } from "@/utils/apiUtils";
import { AxiosPromise } from "axios";
import { ApiRequestAdapter } from "../../../common/api/ApiRequestAdapter";
import { apiService } from "../../../common/api/apiService";
import { HttpContentType, HttpHeaders, HttpMethod } from "../../../common/api/http";
import { PageSizes } from "../../../common/constants";
import { ExportFileType } from "../../../common/enums";
import {
  EntityIdObject,
  EntityObject,
  OptimisticLockVersion,
  OptimisticLockVersionList,
  ThreeLevelEntityObject,
  TwoLevelEntityIdObject,
  TwoLevelEntityObject,
} from "../../../common/types";
import { AGENT_FILTER_INCLUSION_DEFAULTS, COMMON_AGENT_FILTER_QUERY_PARAMS } from "../../agent/api";
import { BailAccountMovement } from "../bailaccount/types";
import { ManualDebtAccountMovementsReport } from "../debtaccount/enums";
import {
  DebtAccountMovement,
  ManualDebtAccountMovementsFilterPageRequest,
  ManualDebtAccountMovementsFilterPageResult,
} from "../debtaccount/types";
import { SpecialCommissionsReport } from "../special/enums";
import {
  SpecialCommission,
  SpecialCommissionsFilterPageRequest,
  SpecialCommissionsFilterPageResult,
} from "../special/types";
import { CommissionOriginType, CommissionProcessingResult, ManualBailAccountMovementsReport } from "./enums";
import {
  BatchPayments,
  CalculateBatchPayments,
  ChangeCommissionsBatchStepRequest,
  Commission,
  CommissionsBatch,
  CommissionsBatchFilterPageRequest,
  CommissionsBatchFilterPageResult,
  CommissionsBatchOutputFilterPageRequest,
  CommissionsBatchOutputFilterPageResult,
  CommissionsFilterPageRequest,
  CommissionsFilterPageResult,
  CommissionsInput,
  CommissionsUnit,
  CreateCommissionsBatch,
  CreateCommissionsUnit,
  CreateManuallyAddedCommission,
  DeleteCommissionsUnitRequest,
  DownloadBatchOutputsAsZip,
  ExcludeSpecialCommission,
  GenerateBatchPaymentOrder,
  ManualBailAccountMovementsFilterPageRequest,
  ManualBailAccountMovementsFilterPageResult,
  PostponeCommissions,
  RecalculateCommissionsForContractRequest,
  UpdateCalculatedCommissionList,
  UpdateCommission,
  UpdateCommissionsBatch,
  UpdateCommissionsBatchFinishedDate,
  UpdateCommissionsUnit,
} from "./types";

const FILTER_COMMISSIONS_BATCHES = ApiRequestAdapter.create("/commissions-batches", HttpMethod.GET);
const GET_COMMISSIONS_BATCH = ApiRequestAdapter.create("/commissions-batches/{0}", HttpMethod.GET);
const CALCULATE_COMMISSIONS_BATCH_PAYMENTS = ApiRequestAdapter.create(
  "/commissions-batches/{0}/calculate-payment-to-be-paid",
  HttpMethod.POST,
);
const GENERATE_COMMISSIONS_BATCH_PAYMENT_ORDER = ApiRequestAdapter.create(
  "/commissions-batches/{0}/generate-payment-order",
  HttpMethod.POST,
);
const DOWNLOAD_COMMISSIONS_BATCH_OUTPUTS_AS_ZIP = ApiRequestAdapter.create(
  "/commissions-batches/{0}/download-outputs-zip",
  HttpMethod.GET,
);
const DOWNLOAD_COMMISSIONS_BATCH_OUTPUTS_EXPORT = ApiRequestAdapter.create(
  "/commissions-batches/{0}/download-outputs-export",
  HttpMethod.GET,
);
const DOWNLOAD_COMMISSIONS_BATCH_INPUTS_EXPORT = ApiRequestAdapter.create(
  "/commissions-batches/{0}/download-inputs-export",
  HttpMethod.GET,
);
const DOWNLOAD_COMMISSIONS_BATCH_INACTIVE_AGENTS_EXPORT = ApiRequestAdapter.create(
  "/commissions-batches/{0}/download-inactive-agents-export",
  HttpMethod.GET,
);
const GET_COMMISSIONS_BATCH_NAME_PREFIX = ApiRequestAdapter.create("/commissions-batches/name-prefix", HttpMethod.GET);
const CREATE_COMMISSIONS_BATCH = ApiRequestAdapter.create("/commissions-batches", HttpMethod.POST);
const UPDATE_COMMISSIONS_BATCH = ApiRequestAdapter.create("/commissions-batches/{0}", HttpMethod.PUT);
const UPDATE_COMMISSIONS_BATCH_FINISHED_DATE = ApiRequestAdapter.create(
  "/commissions-batches/{0}/finished-date",
  HttpMethod.PUT,
);
const DELETE_COMMISSIONS_BATCH = ApiRequestAdapter.create("/commissions-batches/{0}", HttpMethod.DELETE);
const CHANGE_COMMISSIONS_BATCH_STEP = ApiRequestAdapter.create("/commissions-batches/{0}/change-step", HttpMethod.POST);

const CREATE_COMMISSIONS_UNIT = ApiRequestAdapter.create("/commissions-batches/{0}/units", HttpMethod.POST);
const UPDATE_COMMISSIONS_UNIT = ApiRequestAdapter.create("/commissions-batches/{0}/units/{1}", HttpMethod.PUT);
const DELETE_COMMISSIONS_UNIT = ApiRequestAdapter.create("/commissions-batches/{0}", HttpMethod.DELETE);

const UPLOAD_COMMISSIONS_BATCH_INPUT = ApiRequestAdapter.create("/commissions-batches/{0}/inputs", HttpMethod.POST);
const REPLACE_COMMISSIONS_BATCH_INPUT = ApiRequestAdapter.create("/commissions-batches/{0}/inputs/{1}", HttpMethod.PUT);
const DOWNLOAD_COMMISSIONS_BATCH_INPUT = ApiRequestAdapter.create(
  "/commissions-batches/{0}/inputs/{1}",
  HttpMethod.GET,
);
const DELETE_COMMISSIONS_BATCH_INPUT = ApiRequestAdapter.create(
  "/commissions-batches/{0}/inputs/{1}",
  HttpMethod.DELETE,
);
const FILTER_COMMISSIONS_BATCH_OUTPUTS = ApiRequestAdapter.create("/commissions-batches/{0}/outputs", HttpMethod.GET);
const DOWNLOAD_COMMISSIONS_BATCH_OUTPUT = ApiRequestAdapter.create(
  "/commissions-batches/{0}/outputs/{1}",
  HttpMethod.GET,
);

const FILTER_COMMISSIONS = ApiRequestAdapter.create("/commissions-batches/{0}/commissions", HttpMethod.GET);
const CREATE_COMMISSION = ApiRequestAdapter.create("/commissions-batches/{0}/commissions", HttpMethod.POST);
const UPDATE_COMMISSION = ApiRequestAdapter.create("/commissions-batches/{0}/commissions/{1}", HttpMethod.PUT);
const POSTPONE_COMMISSION = ApiRequestAdapter.create("/commissions-batches/{0}/commissions/postpone", HttpMethod.PUT);
const TRY_TO_INCLUDE_COMMISSION = ApiRequestAdapter.create(
  "/commissions-batches/{0}/commissions/include",
  HttpMethod.PUT,
);
const RECALCULATE_COMMISSIONS_FOR_CONTRACT = ApiRequestAdapter.create(
  "/commissions-batches/{0}/commissions/recalculate-for-contract",
  HttpMethod.PUT,
);
const UPDATE_CALCULATED_COMMISSIONS = ApiRequestAdapter.create(
  "/commissions-batches/{0}/commissions/{1}/calculations",
  HttpMethod.PUT,
);
const DELETE_COMMISSION = ApiRequestAdapter.create("/commissions-batches/{0}/commissions/{1}", HttpMethod.DELETE);

const FILTER_MANUAL_BAIL_ACCOUNT_MOVEMENTS = ApiRequestAdapter.create(
  "/commissions-batches/{0}/manual-bail-account-movements",
  HttpMethod.GET,
);
const ASSOCIATE_BAIL_ACCOUNT_MOVEMENT_TO_BATCH = ApiRequestAdapter.create(
  "/commissions-batches/{0}/manual-bail-account-movements/{1}/associate",
  HttpMethod.PUT,
);
const DISASSOCIATE_BAIL_ACCOUNT_MOVEMENT_FROM_BATCH = ApiRequestAdapter.create(
  "/commissions-batches/{0}/manual-bail-account-movements/{1}/disassociate",
  HttpMethod.PUT,
);

const FILTER_MANUAL_DEBT_ACCOUNT_MOVEMENTS = ApiRequestAdapter.create(
  "/commissions-batches/{0}/manual-debt-account-movements",
  HttpMethod.GET,
);
const ASSOCIATE_MANUAL_DEBT_ACCOUNT_MOVEMENT_TO_BATCH = ApiRequestAdapter.create(
  "/commissions-batches/{0}/manual-debt-account-movements/{1}/associate",
  HttpMethod.PUT,
);
const DISASSOCIATE_MANUAL_DEBT_ACCOUNT_MOVEMENT_FROM_BATCH = ApiRequestAdapter.create(
  "/commissions-batches/{0}/manual-debt-account-movements/{1}/disassociate",
  HttpMethod.PUT,
);

const FILTER_SPECIAL_COMMISSIONS = ApiRequestAdapter.create(
  "/commissions-batches/{0}/agents/{1}/special-commissions",
  HttpMethod.GET,
);
const EXCLUDE_SPECIAL_COMMISSION = ApiRequestAdapter.create(
  "/commissions-batches/{0}/agents/{1}/special-commissions/{2}/exclude",
  HttpMethod.PUT,
);
const INCLUDE_SPECIAL_COMMISSION = ApiRequestAdapter.create(
  "/commissions-batches/{0}/agents/{1}/special-commissions/{2}/include",
  HttpMethod.PUT,
);

const IMPORT_SPECIAL_COMMISSIONS = ApiRequestAdapter.create(
  "/commissions-batches/{0}/special-commissions/import",
  HttpMethod.POST,
);

export const commissionBatchApi = {
  filterCommissionsBatches: (
    request: CommissionsBatchFilterPageRequest,
  ): AxiosPromise<CommissionsBatchFilterPageResult> => {
    return apiService.get(FILTER_COMMISSIONS_BATCHES.url, {
      params: request,
      paramsSerializer: { serialize: serializeParams },
    });
  },
  getCommissionsBatch: (request: EntityIdObject): AxiosPromise<CommissionsBatch> => {
    return apiService.get(GET_COMMISSIONS_BATCH.getParametrizedUrl(request.id));
  },
  calculateCommissionsBatchPayments: (request: EntityObject<CalculateBatchPayments>): AxiosPromise<BatchPayments> => {
    return apiService.post(CALCULATE_COMMISSIONS_BATCH_PAYMENTS.getParametrizedUrl(request.id), request.object);
  },
  generateCommissionsBatchPaymentOrder: (request: EntityObject<GenerateBatchPaymentOrder>): AxiosPromise<Blob> => {
    return apiService.post(GENERATE_COMMISSIONS_BATCH_PAYMENT_ORDER.getParametrizedUrl(request.id), request.object);
  },
  downloadCommissionsBatchOutputsAsZip: (request: EntityObject<DownloadBatchOutputsAsZip>): AxiosPromise<Blob> => {
    return apiService.get(DOWNLOAD_COMMISSIONS_BATCH_OUTPUTS_AS_ZIP.getParametrizedUrl(request.id), {
      params: request.object,
      paramsSerializer: { serialize: serializeParams },
      responseType: "blob",
    });
  },
  downloadCommissionsBatchOutputsExport: (request: EntityIdObject): AxiosPromise<Blob> => {
    return apiService.get(DOWNLOAD_COMMISSIONS_BATCH_OUTPUTS_EXPORT.getParametrizedUrl(request.id), {
      responseType: "blob",
    });
  },
  downloadCommissionsBatchInputsExport: (
    request: EntityObject<{ exportFileType: ExportFileType }>,
  ): AxiosPromise<Blob> => {
    return apiService.get(DOWNLOAD_COMMISSIONS_BATCH_INPUTS_EXPORT.getParametrizedUrl(request.id), {
      params: request.object,
      paramsSerializer: { serialize: serializeParams },
      responseType: "blob",
    });
  },
  downloadCommissionsBatchInactiveAgentsExport: (
    request: EntityObject<{ exportFileType: ExportFileType }>,
  ): AxiosPromise<Blob> => {
    return apiService.get(DOWNLOAD_COMMISSIONS_BATCH_INACTIVE_AGENTS_EXPORT.getParametrizedUrl(request.id), {
      params: request.object,
      paramsSerializer: { serialize: serializeParams },
      responseType: "blob",
    });
  },
  getCommissionsBatchNamePrefix: (): AxiosPromise<string> => {
    return apiService.get(GET_COMMISSIONS_BATCH_NAME_PREFIX.url);
  },
  createCommissionsBatch: (request: CreateCommissionsBatch): AxiosPromise<CommissionsBatch> => {
    return apiService.post(CREATE_COMMISSIONS_BATCH.url, request);
  },
  updateCommissionsBatch: (request: EntityObject<UpdateCommissionsBatch>): AxiosPromise<CommissionsBatch> => {
    return apiService.put(UPDATE_COMMISSIONS_BATCH.getParametrizedUrl(request.id), request.object);
  },
  updateCommissionsBatchFinishedDate: (
    request: EntityObject<UpdateCommissionsBatchFinishedDate>,
  ): AxiosPromise<CommissionsBatch> => {
    return apiService.put(UPDATE_COMMISSIONS_BATCH_FINISHED_DATE.getParametrizedUrl(request.id), request.object);
  },
  deleteCommissionsBatch: (request: EntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_COMMISSIONS_BATCH.getParametrizedUrl(request.id));
  },
  changeCommissionsBatchStep: (
    request: EntityObject<ChangeCommissionsBatchStepRequest>,
  ): AxiosPromise<CommissionsBatch> => {
    return apiService.post(CHANGE_COMMISSIONS_BATCH_STEP.getParametrizedUrl(request.id), request.object);
  },
  createCommissionsUnit: (request: EntityObject<CreateCommissionsUnit>): AxiosPromise<CommissionsUnit> => {
    return apiService.post(CREATE_COMMISSIONS_UNIT.getParametrizedUrl(request.id), request.object);
  },
  updateCommissionsUnit: (request: TwoLevelEntityObject<UpdateCommissionsUnit>): AxiosPromise<CommissionsUnit> => {
    return apiService.put(UPDATE_COMMISSIONS_UNIT.getParametrizedUrl(request.id1, request.id2), request.object);
  },
  deleteCommissionsUnit: (request: EntityObject<DeleteCommissionsUnitRequest>): AxiosPromise => {
    return apiService.delete(DELETE_COMMISSIONS_UNIT.getParametrizedUrl(request.id), { params: request.object });
  },
  uploadCommissionsBatchInput: (request: EntityObject<FormData>): AxiosPromise<CommissionsInput> => {
    return apiService.post(UPLOAD_COMMISSIONS_BATCH_INPUT.getParametrizedUrl(request.id), request.object, {
      headers: { [HttpHeaders.CONTENT_TYPE]: HttpContentType.MULTIPART_FORM_DATA },
    });
  },
  replaceCommissionsBatchInput: (request: TwoLevelEntityObject<FormData>): AxiosPromise<CommissionsInput> => {
    return apiService.put(
      REPLACE_COMMISSIONS_BATCH_INPUT.getParametrizedUrl(request.id1, request.id2),
      request.object,
      { headers: { [HttpHeaders.CONTENT_TYPE]: HttpContentType.MULTIPART_FORM_DATA } },
    );
  },
  downloadCommissionsBatchInput: (request: TwoLevelEntityIdObject): AxiosPromise<Blob> => {
    return apiService.get(DOWNLOAD_COMMISSIONS_BATCH_INPUT.getParametrizedUrl(request.id1, request.id2), {
      responseType: "blob",
    });
  },
  deleteCommissionsBatchInput: (request: TwoLevelEntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_COMMISSIONS_BATCH_INPUT.getParametrizedUrl(request.id1, request.id2));
  },
  filterCommissionsBatchOutputs: (
    request: EntityObject<CommissionsBatchOutputFilterPageRequest>,
  ): AxiosPromise<CommissionsBatchOutputFilterPageResult> => {
    return apiService.get(FILTER_COMMISSIONS_BATCH_OUTPUTS.getParametrizedUrl(request.id), {
      params: request.object,
      paramsSerializer: { serialize: serializeParams },
    });
  },
  downloadCommissionsBatchOutput: (request: TwoLevelEntityIdObject): AxiosPromise<Blob> => {
    return apiService.get(DOWNLOAD_COMMISSIONS_BATCH_OUTPUT.getParametrizedUrl(request.id1, request.id2), {
      responseType: "blob",
    });
  },
  filterCommissions: (
    request: EntityObject<CommissionsFilterPageRequest>,
  ): AxiosPromise<CommissionsFilterPageResult> => {
    return apiService.get(FILTER_COMMISSIONS.getParametrizedUrl(request.id), {
      params: request.object,
      paramsSerializer: { serialize: serializeParams },
    });
  },
  createCommission: (request: EntityObject<CreateManuallyAddedCommission>): AxiosPromise<Commission> => {
    return apiService.post(CREATE_COMMISSION.getParametrizedUrl(request.id), request.object);
  },
  updateCommission: (request: TwoLevelEntityObject<UpdateCommission>): AxiosPromise<Commission> => {
    return apiService.put(UPDATE_COMMISSION.getParametrizedUrl(request.id1, request.id2), request.object);
  },
  postponeCommission: (request: EntityObject<PostponeCommissions>): AxiosPromise<Commission[]> => {
    return apiService.put(POSTPONE_COMMISSION.getParametrizedUrl(request.id), request.object);
  },
  tryToIncludeCommission: (request: EntityObject<OptimisticLockVersionList>): AxiosPromise<Commission[]> => {
    return apiService.put(TRY_TO_INCLUDE_COMMISSION.getParametrizedUrl(request.id), request.object);
  },
  recalculateCommissionsForContract: (
    request: EntityObject<RecalculateCommissionsForContractRequest>,
  ): AxiosPromise<Commission[]> => {
    return apiService.put(RECALCULATE_COMMISSIONS_FOR_CONTRACT.getParametrizedUrl(request.id), null, {
      params: { contractId: request.object.contractId },
    });
  },
  updateCalculatedCommissions: (
    request: TwoLevelEntityObject<UpdateCalculatedCommissionList>,
  ): AxiosPromise<Commission> => {
    return apiService.put(UPDATE_CALCULATED_COMMISSIONS.getParametrizedUrl(request.id1, request.id2), request.object);
  },
  deleteCommission: (request: TwoLevelEntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_COMMISSION.getParametrizedUrl(request.id1, request.id2));
  },
  filterManualBailAccountsMovements: (
    request: EntityObject<ManualBailAccountMovementsFilterPageRequest>,
  ): AxiosPromise<ManualBailAccountMovementsFilterPageResult> => {
    return apiService.get(FILTER_MANUAL_BAIL_ACCOUNT_MOVEMENTS.getParametrizedUrl(request.id), {
      params: request.object,
      paramsSerializer: { serialize: serializeParams },
    });
  },
  associateBailAccountMovementToBatch: (
    request: TwoLevelEntityObject<OptimisticLockVersion>,
  ): AxiosPromise<BailAccountMovement> => {
    return apiService.put(
      ASSOCIATE_BAIL_ACCOUNT_MOVEMENT_TO_BATCH.getParametrizedUrl(request.id1, request.id2),
      request.object,
    );
  },
  disassociateBailAccountMovementFromBatch: (
    request: TwoLevelEntityObject<OptimisticLockVersion>,
  ): AxiosPromise<BailAccountMovement> => {
    return apiService.put(
      DISASSOCIATE_BAIL_ACCOUNT_MOVEMENT_FROM_BATCH.getParametrizedUrl(request.id1, request.id2),
      request.object,
    );
  },
  filterManualDebtAccountMovements: (
    request: EntityObject<ManualDebtAccountMovementsFilterPageRequest>,
  ): AxiosPromise<ManualDebtAccountMovementsFilterPageResult> => {
    return apiService.get(FILTER_MANUAL_DEBT_ACCOUNT_MOVEMENTS.getParametrizedUrl(request.id), {
      params: request.object,
      paramsSerializer: { serialize: serializeParams },
    });
  },
  associateManualDebtAccountMovementToBatch: (
    request: TwoLevelEntityObject<OptimisticLockVersion>,
  ): AxiosPromise<DebtAccountMovement> => {
    return apiService.put(
      ASSOCIATE_MANUAL_DEBT_ACCOUNT_MOVEMENT_TO_BATCH.getParametrizedUrl(request.id1, request.id2),
      request.object,
    );
  },
  disassociateManualDebtAccountMovementFromBatch: (
    request: TwoLevelEntityObject<OptimisticLockVersion>,
  ): AxiosPromise<DebtAccountMovement> => {
    return apiService.put(
      DISASSOCIATE_MANUAL_DEBT_ACCOUNT_MOVEMENT_FROM_BATCH.getParametrizedUrl(request.id1, request.id2),
      request.object,
    );
  },
  filterSpecialCommissions: (
    request: TwoLevelEntityObject<SpecialCommissionsFilterPageRequest>,
  ): AxiosPromise<SpecialCommissionsFilterPageResult<SpecialCommission>> => {
    return apiService.get(FILTER_SPECIAL_COMMISSIONS.getParametrizedUrl(request.id1, request.id2), {
      params: request.object,
    });
  },
  excludeSpecialCommission: (
    request: ThreeLevelEntityObject<ExcludeSpecialCommission>,
  ): AxiosPromise<SpecialCommission> => {
    return apiService.put(
      EXCLUDE_SPECIAL_COMMISSION.getParametrizedUrl(request.id1, request.id2, request.id3),
      request.object,
    );
  },
  includeSpecialCommission: (
    request: ThreeLevelEntityObject<OptimisticLockVersion>,
  ): AxiosPromise<SpecialCommission> => {
    return apiService.put(
      INCLUDE_SPECIAL_COMMISSION.getParametrizedUrl(request.id1, request.id2, request.id3),
      request.object,
    );
  },
  importSpecialCommissions: (request: EntityObject<FormData>): AxiosPromise<Blob> => {
    return apiService.post(IMPORT_SPECIAL_COMMISSIONS.getParametrizedUrl(request.id), request.object, {
      headers: { [HttpHeaders.CONTENT_TYPE]: HttpContentType.MULTIPART_FORM_DATA },
      responseType: "blob",
    });
  },
};

export const DEFAULT_FILTER_COMMISSIONS_BATCHES_QUERY_PARAMS: Required<CommissionsBatchFilterPageRequest> = {
  pageIndex: 0,
  pageSize: PageSizes.LARGE,
  keyword: "",
  steps: [],
};

export const DEFAULT_FILTER_MANUAL_BAIL_ACCOUNT_MOVEMENTS_QUERY_PARAMS: Required<ManualBailAccountMovementsFilterPageRequest> =
  {
    report: ManualBailAccountMovementsReport.ASSOCIATED,
    pageIndex: 0,
    pageSize: PageSizes.HUGE,
  };

export const DEFAULT_FILTER_MANUAL_DEBT_ACCOUNT_MOVEMENTS_QUERY_PARAMS: Required<ManualDebtAccountMovementsFilterPageRequest> =
  {
    report: ManualDebtAccountMovementsReport.ASSOCIATED,
    pageIndex: 0,
    pageSize: PageSizes.HUGE,
  };

export const DEFAULT_FILTER_SPECIAL_COMMISSIONS_QUERY_PARAMS: Required<SpecialCommissionsFilterPageRequest> = {
  pageIndex: 0,
  pageSize: PageSizes.LARGE,
  report: SpecialCommissionsReport.INCLUDED,
  code: "",
};

export const DEFAULT_FILTER_COMMISSIONS_BATCH_OUTPUTS_QUERY_PARAMS: Required<CommissionsBatchOutputFilterPageRequest> =
  {
    ...COMMON_AGENT_FILTER_QUERY_PARAMS,
    ...AGENT_FILTER_INCLUSION_DEFAULTS.NO_REPRESENTATIVES,
    categories: [],
  };

export const DEFAULT_FILTER_COMMISSIONS_QUERY_PARAMS: Required<CommissionsFilterPageRequest> = {
  pageIndex: 0,
  pageSize: PageSizes.HUGE,
  keyword: "",
  processingResult: CommissionProcessingResult.ALL,
  origin: CommissionOriginType.ALL,
  postponementReasons: [],
  sourceIds: [],
  institutionIds: [],
};
